import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import moment from 'moment';
import { partial } from '../../../util/translation/translation';
import { formatDateTime } from '../../../util/date-and-time';
import {
  bookingTotal,
  canExpireAtRegistrationEnd,
  expiresAtFor,
  formatMoney,
  isValidKrInput,
  krInputToOre,
  oreToKrInput,
} from '../payments';

/**
 * Price and deadline for a place someone is to pay for: a waitlist offer, or
 * a new payment link. Prefilled with the event's price and fee; the deadline
 * is a quick pick ("default" = the event's time to pay, offers only) or a
 * date. Confirms with { price, bookingFee, expiresAt } in øre / ISO.
 */
const PaymentOfferModal = ({
  isOpen,
  title,
  description,
  event,
  currency,
  places,
  defaultPrice,
  defaultBookingFee,
  allowDefaultExpiry,
  confirmLabel,
  busy,
  onClose,
  onConfirm,
}) => {
  const p = partial('EventPayments');
  const s = partial('shared');
  const [price, setPrice] = useState('');
  const [bookingFee, setBookingFee] = useState('');
  const [choice, setChoice] = useState('default');
  const [custom, setCustom] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPrice(oreToKrInput(defaultPrice ?? event?.price));
      setBookingFee(oreToKrInput(defaultBookingFee ?? event?.bookingFee ?? 0));
      setChoice(allowDefaultExpiry ? 'default' : '24h');
      setCustom('');
      setError('');
    }
  }, [isOpen]);

  const choices = [
    ...(allowDefaultExpiry
      ? [
          {
            key: 'default',
            label: p('expiryDefault', { hours: event?.offerHours || 12 }),
          },
        ]
      : []),
    { key: '24h', label: p('expiry24h') },
    { key: '3d', label: p('expiry3d') },
    ...(canExpireAtRegistrationEnd(event)
      ? [{ key: 'registrationEnd', label: p('expiryRegistrationEnd') }]
      : []),
    { key: 'custom', label: p('expiryCustom') },
  ];

  const priceOre = krInputToOre(price) || 0;
  const feeOre = krInputToOre(bookingFee) || 0;

  const confirm = () => {
    if (!isValidKrInput(price) || !isValidKrInput(bookingFee)) {
      setError(p('errorAmount'));
      return;
    }
    if (choice === 'custom') {
      if (!custom || !moment(custom).isAfter(moment())) {
        setError(p('errorExpiryPast'));
        return;
      }
    }
    onConfirm({
      price: priceOre,
      bookingFee: feeOre,
      expiresAt: expiresAtFor(choice, custom, event),
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {description && (
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            {description}
          </p>
        )}
        <div className="event-payments__modal-fields">
          <div>
            <label className="form-header d-block mb-1" htmlFor="offerPrice">
              {p('pricePerPlace', { currency })}
            </label>
            <Input
              id="offerPrice"
              inputMode="decimal"
              value={price}
              onChange={(e) => {
                setPrice(e.target.value);
                setError('');
              }}
            />
          </div>
          <div>
            <label className="form-header d-block mb-1" htmlFor="offerFee">
              {p('bookingFee', { currency })}
            </label>
            <Input
              id="offerFee"
              inputMode="decimal"
              value={bookingFee}
              onChange={(e) => {
                setBookingFee(e.target.value);
                setError('');
              }}
            />
          </div>
        </div>
        <p className="event-payments__total">
          {p('totalForPlaces', {
            count: places,
            total: formatMoney(
              bookingTotal(places, priceOre, feeOre),
              currency
            ),
          })}
        </p>
        <span className="form-header d-block mb-1">{p('payBefore')}</span>
        <div className="event-payments__picks" role="radiogroup">
          {choices.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={choice === key}
              className={`stats-range${choice === key ? ' active' : ''}`}
              onClick={() => {
                setChoice(key);
                setError('');
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {choice === 'custom' && (
          <Input
            className="mt-2"
            type="datetime-local"
            aria-label={p('expiryCustom')}
            value={custom}
            min={moment().format('YYYY-MM-DDTHH:mm')}
            onChange={(e) => {
              setCustom(e.target.value);
              setError('');
            }}
          />
        )}
        {choice !== 'default' && choice !== 'custom' && (
          <small className="event-payments__hint">
            {p('payBeforeDate', {
              date: formatDateTime(expiresAtFor(choice, custom, event)),
            })}
          </small>
        )}
        {error && (
          <span className="form__form-group-error d-block mt-2">{error}</span>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" className="mb-0" onClick={onClose}>
          {s('cancel')}
        </Button>
        <Button
          color="primary"
          className="mb-0"
          disabled={busy}
          onClick={confirm}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PaymentOfferModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  event: PropTypes.shape({
    price: PropTypes.number,
    bookingFee: PropTypes.number,
    offerHours: PropTypes.number,
    registrationEnd: PropTypes.string,
  }),
  currency: PropTypes.string,
  places: PropTypes.number,
  defaultPrice: PropTypes.number,
  defaultBookingFee: PropTypes.number,
  allowDefaultExpiry: PropTypes.bool,
  confirmLabel: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

PaymentOfferModal.defaultProps = {
  description: '',
  event: null,
  currency: '',
  places: 1,
  defaultPrice: undefined,
  defaultBookingFee: undefined,
  allowDefaultExpiry: false,
  busy: false,
};

export default PaymentOfferModal;
